<script setup lang="ts">
import LitepieDatepicker from 'litepie-datepicker-tw3'

import { ExternalLink } from 'lucide-vue-next'

import AudiencePreview from '@/components/audiences/AudiencePreview.vue'
import EmailTemplatePreview from '@/views/email/components/EmailTemplatePreview.vue'

import type { SelectedAudience } from '@/components/audiences/Audience.vue'

const count = defineModel<number>('count')
const isLoadingCount = defineModel<boolean>('isLoadingCount')
const launchChoice = defineModel<string>('launchChoice')
const dateCampaign = defineModel<string | null>('dateCampaign')
const timeCampaign = defineModel<{ hour: number | null; minute: number | null }>('timeCampaign')
const selectedAudience = defineModel<SelectedAudience>('selectedAudience')

const props = withDefaults(
  defineProps<{
    name: string
    emailContentId: number | null
    selectedTemplateName: string | null
    emailSubject: string
    emailPreviewText: string
    userStoresAudience: number
    userStoresCount: number
    isProcessing: boolean
    isSavingDraft?: boolean
    hasRequiredMandate?: boolean
    hasEmailMandate?: boolean
    canLaunchEmailCampaign?: boolean
    senderDomainVerified?: boolean
    senderDomainId?: number | null
  }>(),
  {
    isSavingDraft: false,
    hasRequiredMandate: true,
    hasEmailMandate: true,
    canLaunchEmailCampaign: true,
    senderDomainVerified: true,
    senderDomainId: null
  }
)

const emit = defineEmits<{
  (e: 'onCreate'): void
  (e: 'onSaveDraft'): void
}>()

const { t } = useI18n()
const { isDirection } = useAccess()
const { pad } = useText()
const router = useRouter()

const disableDate = (date: Date) => {
  const now = new Date().setHours(0, 0, 0, 0)
  return Number(date) < now || date.getDay() === 0
}

// LitepieDatepicker treats `typeof modelValue === 'object'` as range mode,
// which is true for `null` — and crashes on Object.keys(null). Pass '' instead.
const datePickerValue = computed({
  get: () => dateCampaign.value ?? '',
  set: (value: string) => {
    dateCampaign.value = value || null
  }
})

const formatHoursAndMinutes = (number: number) => {
  return pad(number, 2)
}

const launchChoices = computed(() => [
  {
    id: 'now',
    title: t('pages.campaignEmail.create.validation.launchNow'),
    description: t('pages.campaignEmail.create.validation.launchNowDescription')
  },
  {
    id: 'later',
    title: t('pages.campaignEmail.create.validation.launchLater'),
    description: t('pages.campaignEmail.create.validation.launchLaterDescription')
  }
])

const selectedLaunch = computed({
  get: () => launchChoices.value.find((c) => c.id === launchChoice.value),
  set: (opt) => (launchChoice.value = opt?.id as string)
})

const openBillingSettings = () => {
  const route = router.resolve({ name: 'InvoiceList' })
  window.open(route.href, '_blank')
}

const openDomainConfig = () => {
  router.push({
    name: 'DomainConfig',
    ...(props.senderDomainId ? { query: { domainId: String(props.senderDomainId) } } : {})
  })
}

const canCreate = computed(() => {
  if (!props.canLaunchEmailCampaign) return false
  if (!props.senderDomainVerified) return false
  if (!props.emailContentId) return false
  if (!props.name) return false

  if (launchChoice.value === 'later') {
    if (!dateCampaign.value) return false
    if (timeCampaign.value.hour === null || timeCampaign.value.minute === null) return false
  }

  return true
})
</script>

<template>
  <div class="flex w-full flex-col gap-6 pb-6 xl:grid xl:grid-cols-12 px-12">
    <section class="flex flex-col gap-6 col-span-8">
      <BaseTip v-if="!hasRequiredMandate" severity="warn">
        <template #text>
          <p class="font-medium">{{ t('pages.campaignEmail.create.validation.mandateRequired.title') }}</p>
          <p>{{ t('pages.campaignEmail.create.validation.mandateRequired.description') }}</p>
        </template>
        <template #button>
          <Button variant="outline" class="bg-white" @click="openBillingSettings">
            {{ t('pages.campaignEmail.create.validation.mandateRequired.cta') }}
            <ExternalLink />
          </Button>
        </template>
      </BaseTip>

      <BaseTip v-else-if="!hasEmailMandate" severity="warn">
        <template #text>
          <p class="font-medium">{{ t('pages.campaignEmail.create.validation.emailMandateRequired.title') }}</p>
          <p>{{ t('pages.campaignEmail.create.validation.emailMandateRequired.description') }}</p>
        </template>
      </BaseTip>

      <BaseTip v-else-if="!senderDomainVerified" severity="warn">
        <template #text>
          <p class="font-medium">{{ t('pages.campaignEmail.domainNotVerified.title') }}</p>
          <p>{{ t('pages.campaignEmail.domainNotVerified.description') }}</p>
        </template>
        <template v-if="isDirection" #button>
          <Button variant="outline" class="bg-white" @click="openDomainConfig">
            {{ t('pages.campaignEmail.domainNotVerified.cta') }}
          </Button>
        </template>
      </BaseTip>

      <div class="rounded-md border bg-white">
        <div class="w-full border-b p-6 text-base font-semibold sm:text-lg">
          {{ t('pages.campaignEmail.create.validation.launchConfig') }}
        </div>
        <div class="p-6">
          <div class="flex flex-col gap-4">
            <AppRadioCard v-model="selectedLaunch" :options="launchChoices" />

            <div v-if="launchChoice === 'later'" class="flex flex-col rounded-md text-base text-foreground md:flex-row">
              <div class="flex flex-col text-base text-foreground">
                <p class="mb-1 text-left text-sm text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.block.launch.date') }}
                </p>
                <LitepieDatepicker
                  v-model="datePickerValue"
                  :disable-date="disableDate"
                  class="rounded-sm pr-2!"
                  as-single
                  i18n="fr"
                  :formatter="{
                    date: 'DD/MM/YYYY',
                    month: 'MMM'
                  }"
                />
              </div>
              <div class="my-5 flex flex-col md:my-0 md:ml-2">
                <p class="mb-1 text-left text-sm text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.block.launch.hour') }}
                </p>
                <div class="flex gap-1">
                  <Select v-model="timeCampaign.hour">
                    <SelectTrigger class="w-auto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="hour in 24" :key="hour" :value="hour - 1">
                        {{ formatHoursAndMinutes(hour - 1) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Select v-model="timeCampaign.minute">
                    <SelectTrigger class="w-auto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="minute in 60" :key="minute" :value="minute - 1">
                        {{ formatHoursAndMinutes(minute - 1) }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button variant="outline" :disabled="!name || isProcessing || isSavingDraft" @click="emit('onSaveDraft')">
          <Spinner v-if="isSavingDraft" class="mr-2" />
          {{ t('pages.campaignEmail.create.validation.saveAsDraft') }}
        </Button>
        <Button
          variant="default-primary"
          :disabled="!canCreate || isProcessing || isSavingDraft"
          @click="emit('onCreate')"
        >
          <Spinner v-if="isProcessing" class="mr-2 text-white" />
          {{
            launchChoice === 'now'
              ? t('pages.campaignEmail.create.validation.createAndSend')
              : t('pages.campaignEmail.create.validation.createAndSchedule')
          }}
        </Button>
      </div>
    </section>

    <section class="flex flex-col gap-6 col-span-4">
      <div class="sticky top-2 flex flex-col gap-6">
        <AudiencePreview
          :rules="{}"
          :targets-count="count"
          :stores-contacts-count="userStoresAudience"
          :stores-count="userStoresCount"
          :selected-audience="selectedAudience"
          :loading="isLoadingCount"
        >
          <template #title>
            <p class="text-base font-semibold text-foreground">
              {{ t('pages.campaignEmail.create.preview.title') }}
            </p>
          </template>
        </AudiencePreview>

        <div v-if="emailContentId" class="rounded-md border bg-white">
          <div class="border-b border-border p-4">
            <p class="text-base font-semibold text-foreground">
              {{ t('pages.campaignEmail.detail.emailPreview') }}
            </p>
          </div>
          <div class="p-3.5">
            <div v-if="selectedTemplateName" class="mb-3 flex items-center gap-2 rounded-md bg-primary/5 p-3">
              <BaseIcon name="HeroEnvelopeIconSolid" class="h-4 w-4 text-primary" />
              <span class="text-sm text-primary">{{ selectedTemplateName }}</span>
            </div>

            <div class="mb-3 space-y-2 rounded-md border border-border bg-muted p-3 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-muted-foreground">{{ t('pages.email.emailTemplateCreatePage.from') }}</span>
                <span class="text-foreground">contact@heypongo.link</span>
              </div>
              <div v-if="emailSubject" class="flex items-center gap-2">
                <span class="text-muted-foreground">{{ t('pages.email.emailTemplateCreatePage.subject') }}</span>
                <span class="text-foreground">{{ emailSubject }}</span>
              </div>
              <div v-if="emailPreviewText" class="flex items-center gap-2">
                <span class="text-muted-foreground">{{ t('pages.email.emailTemplateCreatePage.previewText') }}</span>
                <span class="text-foreground">{{ emailPreviewText }}</span>
              </div>
            </div>

            <EmailTemplatePreview :template-id="String(emailContentId)" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
